import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Kpi, StatusBadge, Empty } from "@/components/ui-bits";
import { fmtNum, fmtINR, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Buildings, Pause, Play, Eye, PencilSimple, UploadSimple, Trash, ArrowCounterClockwise, DownloadSimple, Database, Coins } from "@phosphor-icons/react";

export default function Tenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [view, setView] = useState("active");
  const isBin = view === "deleted";
  const industryName = (key) => industries.find((i) => i.key === key)?.name || "NBFC / Lender";

  const load = () => {
    api.get("/tenants", { params: { deleted: isBin } }).then((r) => setTenants(r.data)).catch(() => {});
    api.get("/platform/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [view]);
  useEffect(() => { api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {}); }, []);

  const toggle = async (t) => {
    await api.patch(`/tenants/${t.tenant_id}`, { status: t.status === "active" ? "suspended" : "active" });
    toast.success(`Tenant ${t.status === "active" ? "suspended" : "activated"}`);
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Move "${t.company_name}" to the Recycle Bin? Its data will be excluded from all dashboards and reports but can be restored anytime.`)) return;
    await api.delete(`/tenants/${t.tenant_id}`);
    toast.success(`${t.company_name} moved to Recycle Bin`);
    load();
  };

  const restore = async (t) => {
    await api.post(`/tenants/${t.tenant_id}/restore`);
    toast.success(`${t.company_name} restored`);
    load();
  };

  return (
    <div data-testid="tenants-page">
      <PageHeader title="Tenant Management" subtitle="Platform-level controls for all lenders">
        {!isBin && <CreateTenantDialog onDone={load} industries={industries} />}
      </PageHeader>

      <div className="flex gap-2 mb-5">
        <button onClick={() => setView("active")} data-testid="tenants-view-active" className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${!isBin ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary"}`}>Active Tenants</button>
        <button onClick={() => setView("deleted")} data-testid="tenants-view-bin" className={`px-3 py-1.5 text-sm rounded-sm border inline-flex items-center gap-1.5 transition-colors ${isBin ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary"}`}><Trash size={14} weight="bold" /> Recycle Bin</button>
      </div>

      {stats && !isBin && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi label="Tenants" value={fmtNum(stats.tenants)} sub={`${stats.active_tenants} active`} />
          <Kpi label="Total Accounts" value={fmtNum(stats.accounts)} />
          <Kpi label="Platform Users" value={fmtNum(stats.users)} />
          <Kpi label="Communications" value={fmtNum(stats.communications)} />
        </div>
      )}

      {!isBin && <MigrationPanel tenants={tenants} onDone={load} />}

      <div className="grid md:grid-cols-2 gap-4">
        {tenants.map((t) => (
          <div key={t.tenant_id} className="border border-border bg-card p-5" data-testid={`tenant-card-${t.tenant_id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {t.logo ? (
                  <img src={t.logo} alt="" className="h-11 w-11 rounded-sm object-cover border border-border" />
                ) : (
                  <div className="h-11 w-11 rounded-sm bg-primary text-white flex items-center justify-center">
                    <Buildings size={20} weight="bold" />
                  </div>
                )}
                <div>
                  <div className="font-heading font-bold text-lg leading-none">{t.company_name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t.plan} • {t.contact_email}</div>
                  <span className="inline-block mt-1.5 px-2 py-0.5 text-[10px] font-semibold rounded-sm bg-primary/10 text-primary" data-testid={`tenant-industry-${t.tenant_id}`}>{industryName(t.industry || "nbfc")}</span>
                </div>
              </div>
              <StatusBadge status={t.status} />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-center">
              <Metric label="Monthly Fee" value={fmtINR(t.monthly_saas_fee || 0)} />
              <Metric label={isBin ? "Deleted" : "Renewal"} value={isBin ? (t.deleted_at ? fmtDate(t.deleted_at) : "—") : (t.renewal_date ? fmtDate(t.renewal_date) : "—")} />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3 text-center">
              <Metric label="Accounts" value={`${fmtNum(t.account_count)} / ${fmtNum(t.account_limit)}`} />
              <Metric label="Users" value={fmtNum(t.user_count)} />
              <Metric label="Total Available RC" value={`${fmtNum(t.wallet?.total_available_rc ?? 0)} RC`} />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3 text-center" data-testid={`wallet-${t.tenant_id}`}>
              <Metric label="Purchased RC" value={`${fmtNum(t.wallet?.purchased_rc ?? 0)} RC`} />
              <Metric label="Free Monthly RC" value={`${fmtNum(t.wallet?.free_rc_available ?? 0)} / ${fmtNum(t.wallet?.monthly_free_rc_max ?? 0)}`} />
              <Metric label="Next Free RC Reset" value={t.wallet?.next_free_rc_reset ? new Date(t.wallet.next_free_rc_reset).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"} />
            </div>
            {isBin ? (
              <div className="flex gap-2 mt-4">
                <Button size="sm" className="rounded-sm gap-1 flex-1" onClick={() => restore(t)} data-testid={`restore-tenant-${t.tenant_id}`}>
                  <ArrowCounterClockwise size={14} weight="bold" /> Restore Tenant
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 mt-4">
                <Button
                  size="sm" variant="default"
                  className="rounded-sm gap-1 flex-1"
                  onClick={() => navigate(`/tenants/${t.tenant_id}/accounts`)}
                  data-testid={`view-accounts-${t.tenant_id}`}
                >
                  <Eye size={14} weight="bold" /> View Accounts
                </Button>
                <EditTenantDialog tenant={t} onDone={load} industries={industries} />
                <AdjustRcDialog tenant={t} onDone={load} />
                <Button
                  size="sm" variant={t.status === "active" ? "outline" : "default"}
                  className="rounded-sm gap-1"
                  onClick={() => toggle(t)}
                  data-testid={`toggle-tenant-${t.tenant_id}`}
                >
                  {t.status === "active" ? <><Pause size={14} weight="bold" /> Suspend</> : <><Play size={14} weight="bold" /> Activate</>}
                </Button>
                <Button
                  size="sm" variant="outline"
                  className="rounded-sm gap-1 text-destructive hover:bg-destructive/10 border-destructive/30"
                  onClick={() => remove(t)}
                  data-testid={`delete-tenant-${t.tenant_id}`}
                >
                  <Trash size={14} weight="bold" /> Delete
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {tenants.length === 0 && <Empty text={isBin ? "Recycle Bin is empty." : "No tenants yet. Create your first lender."} />}
    </div>
  );
}

function MigrationPanel({ tenants, onDone }) {
  const [open, setOpen] = useState(false);
  const [exportId, setExportId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const doExport = async () => {
    if (!exportId) { toast.error("Select a tenant to export"); return; }
    setExporting(true);
    try {
      const r = await api.get("/admin/export-tenant-bundle", { params: { tenant_id: exportId }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tenant_${exportId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Bundle downloaded. Upload it on your production deployment.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Import "${file.name}"? This will create/overwrite the tenant and its records in THIS environment's database.`)) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/import-tenant-bundle", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      const total = Object.values(r.data.counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Imported tenant ${r.data.tenant_id} — ${total} records`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border border-border bg-card p-4 mb-6" data-testid="migration-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid="migration-toggle"
      >
        <span className="inline-flex items-center gap-2 font-heading font-bold text-sm">
          <Database size={18} weight="bold" className="text-primary" /> Data Migration (Preview → Production)
        </span>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="mt-4 grid md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="text-xs">Export a tenant bundle</Label>
            <p className="text-xs text-muted-foreground">Download a full JSON snapshot (tenant + users + accounts + activities + payments + settlements) to move into another environment.</p>
            <Select value={exportId} onValueChange={setExportId}>
              <SelectTrigger className="rounded-sm" data-testid="migration-export-select"><SelectValue placeholder="Select tenant to export" /></SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.company_name} ({t.tenant_id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="rounded-sm gap-1" onClick={doExport} disabled={exporting} data-testid="migration-export-btn">
              <DownloadSimple size={14} weight="bold" /> {exporting ? "Exporting…" : "Download Bundle"}
            </Button>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Import a tenant bundle</Label>
            <p className="text-xs text-muted-foreground">Upload a JSON bundle exported from another environment. Existing records (matched by id/email) are overwritten.</p>
            <input ref={fileRef} type="file" accept="application/json,.json" onChange={doImport} className="hidden" data-testid="migration-import-input" />
            <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="migration-import-btn">
              <UploadSimple size={14} weight="bold" /> {importing ? "Importing…" : "Upload Bundle"}
            </Button>
            {result && (
              <div className="text-xs bg-secondary p-2 rounded-sm mt-1" data-testid="migration-import-result">
                <div className="font-semibold">Imported tenant: {result.tenant_id}</div>
                {Object.entries(result.counts || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function Metric({ label, value }) {
  return (
    <div className="border border-border p-2">
      <div className="label-caps">{label}</div>
      <div className="text-sm font-semibold mono mt-1">{value}</div>
    </div>
  );
}

function AdjustRcDialog({ tenant, onDone }) {
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [mode, setMode] = useState("debit");   // credit | debit | set
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get(`/tenants/${tenant.tenant_id}/rc-adjustments`)
    .then((r) => setHistory(r.data?.adjustments || [])).catch(() => setHistory([]));

  useEffect(() => {
    if (open) {
      setMode("debit"); setAmount(""); setReason(""); setBalance(null);
      api.get("/connect/credits", { params: { tenant_id: tenant.tenant_id } })
        .then((r) => setBalance(r.data?.credit_balance_rc ?? 0)).catch(() => setBalance(null));
      loadHistory();
    }
  }, [open, tenant.tenant_id]);

  const submit = async () => {
    const amt = Number(amount);
    if (mode === "set") {
      if (amount === "" || amt < 0) return toast.error("Enter a target balance of 0 or more");
    } else if (!amt || amt <= 0) {
      return toast.error("Enter an amount greater than 0");
    }
    if (!reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenant.tenant_id}/rc-adjust`, { amount_rc: amt, direction: mode, reason: reason.trim() });
      toast.success(`New purchased balance: ${fmtNum(r.data.new_balance_rc)} RC`);
      setBalance(r.data.new_balance_rc); setAmount(""); setReason("");
      loadHistory();
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adjustment failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm gap-1" data-testid={`adjust-rc-${tenant.tenant_id}`}>
          <Coins size={14} weight="bold" /> Adjust RC
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="adjust-rc-dialog">
        <DialogHeader>
          <DialogTitle>Adjust RC — {tenant.company_name}</DialogTitle>
          <DialogDescription>
            Corrective adjustment of the tenant's purchased RC balance (immutable ledger entry). Free Monthly RC and plan entitlements are not affected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-sm border border-border bg-secondary/40 p-3 text-sm" data-testid="adjust-rc-current">
            Current purchased RC balance: <span className="font-bold mono">{balance == null ? "…" : `${fmtNum(balance)} RC`}</span>
          </div>
          <div>
            <Label className="text-xs">Action</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[["credit", "Add RC"], ["debit", "Remove RC"], ["set", "Set Balance"]].map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => setMode(v)}
                  className={`rounded-sm border px-2 py-1.5 text-xs font-semibold transition-colors ${mode === v ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"}`}
                  data-testid={`adjust-rc-mode-${v}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">{mode === "set" ? "Target Balance (RC)" : "Amount (RC)"}</Label>
            <Input type="number" min="0" className="rounded-sm" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adjust-rc-amount" />
            {mode === "set" && <p className="text-[11px] text-muted-foreground mt-1">The system computes the delta from the current balance and writes one ledger adjustment.</p>}
            {mode === "debit" && <p className="text-[11px] text-muted-foreground mt-1">A debit larger than the balance is clamped, leaving exactly 0 RC.</p>}
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <Input className="rounded-sm" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Remove legacy opening balance" data-testid="adjust-rc-reason" />
          </div>

          <div className="pt-1">
            <div className="label-caps text-muted-foreground mb-1">Adjustment History</div>
            {history.length === 0 ? (
              <div className="text-xs text-muted-foreground py-2" data-testid="adjust-rc-history-empty">No adjustments yet.</div>
            ) : (
              <div className="border border-border rounded-sm overflow-hidden" data-testid="adjust-rc-history">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/60 text-muted-foreground">
                    <tr><th className="text-left p-2">Date</th><th className="text-right p-2">Amount</th><th className="text-left p-2">Reason</th><th className="text-left p-2">Admin</th></tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2 whitespace-nowrap">{h.date ? new Date(h.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}</td>
                        <td className={`p-2 text-right mono font-semibold ${h.amount_rc < 0 ? "text-red-600" : "text-emerald-700"}`}>{h.amount_rc > 0 ? "+" : ""}{fmtNum(h.amount_rc)}</td>
                        <td className="p-2">{h.reason}</td>
                        <td className="p-2">{h.admin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} data-testid="adjust-rc-cancel">Close</Button>
          <Button className="rounded-sm" disabled={busy} onClick={submit} data-testid="adjust-rc-submit">{busy ? "Saving…" : "Apply Adjustment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BrandingFields({ f, upd, idPrefix }) {
  const presets = ["#1f4dff", "#0d9488", "#dc2626", "#7c3aed", "#ea580c", "#0891b2", "#be123c", "#15803d"];
  return (
    <div className="border border-border rounded-sm p-3 space-y-3 bg-secondary/30">
      <div className="label-caps">White-label Branding</div>
      <div><Label className="text-xs">Product Name (shown in app)</Label>
        <Input className="rounded-sm" placeholder="e.g. City Care Recovery" value={f.product_name || ""} onChange={(e) => upd("product_name", e.target.value)} data-testid={`${idPrefix}-product-name`} />
      </div>
      <div>
        <Label className="text-xs">Accent Color</Label>
        <div className="flex items-center gap-2 mt-1">
          <input type="color" value={f.accent_color || "#1f4dff"} onChange={(e) => upd("accent_color", e.target.value)} className="h-9 w-12 rounded-sm border border-border cursor-pointer bg-transparent" data-testid={`${idPrefix}-accent-color`} />
          <Input className="rounded-sm w-32 mono" placeholder="#1f4dff" value={f.accent_color || ""} onChange={(e) => upd("accent_color", e.target.value)} />
          <div className="flex items-center gap-1.5">
            {presets.map((p) => (
              <button key={p} type="button" onClick={() => upd("accent_color", p)} className="h-6 w-6 rounded-full border border-border" style={{ background: p }} title={p} />
            ))}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">Leave blank to use the default RecoverOS blue.</p>
      </div>
    </div>
  );
}

function LogoUpload({ value, onChange, testId }) {
  const fileRef = useRef(null);
  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB");
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <Label className="text-xs">Logo</Label>
      <div className="flex items-center gap-3 mt-1">
        {value
          ? <img src={value} alt="logo" className="h-12 w-12 rounded-sm object-cover border border-border" />
          : <div className="h-12 w-12 rounded-sm bg-secondary flex items-center justify-center text-muted-foreground"><Buildings size={18} weight="bold" /></div>}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testId} />
        <Button type="button" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => fileRef.current?.click()}>
          <UploadSimple size={14} weight="bold" /> Upload
        </Button>
        {value && <button type="button" className="text-xs text-destructive hover:underline" onClick={() => onChange("")}>Remove</button>}
      </div>
    </div>
  );
}

function CreateTenantDialog({ onDone, industries = [] }) {
  const [open, setOpen] = useState(false);
  const empty = { company_name: "", contact_name: "", contact_email: "", contact_phone: "", logo: "", industry: "nbfc", product_name: "", accent_color: "", plan: "Starter", monthly_saas_fee: "", start_date: "", renewal_date: "", subscription_status: "active", account_limit: "5000", communication_credits: "0" };
  const [f, setF] = useState(empty);
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    if (!f.company_name) return toast.error("Company name required");
    try {
      await api.post("/tenants", { ...f, monthly_saas_fee: +f.monthly_saas_fee || 0, account_limit: +f.account_limit, communication_credits: +f.communication_credits });
      toast.success("Tenant created");
      setOpen(false);
      setF(empty);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm gap-2" data-testid="add-tenant-button"><Plus size={16} weight="bold" /> New Tenant</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Onboard New Tenant</DialogTitle>
          <DialogDescription>Create a lender workspace with isolated data and its own subscription.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Company Name</Label><Input className="rounded-sm" value={f.company_name} onChange={(e) => upd("company_name", e.target.value)} data-testid="tenant-name" /></div>
          <div><Label className="text-xs">Industry / Vertical</Label>
            <Select value={f.industry} onValueChange={(v) => upd("industry", v)}>
              <SelectTrigger className="rounded-sm" data-testid="tenant-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
              <SelectContent>{industries.map((i) => <SelectItem key={i.key} value={i.key}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Drives terminology, enabled modules and custom fields for this workspace.</p>
          </div>
          <BrandingFields f={f} upd={upd} idPrefix="tenant" />
          <LogoUpload value={f.logo} onChange={(v) => upd("logo", v)} testId="tenant-logo" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Contact Name</Label><Input className="rounded-sm" value={f.contact_name} onChange={(e) => upd("contact_name", e.target.value)} /></div>
            <div><Label className="text-xs">Contact Email</Label><Input className="rounded-sm" value={f.contact_email} onChange={(e) => upd("contact_email", e.target.value)} data-testid="tenant-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Contact Mobile</Label><Input className="rounded-sm" value={f.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} /></div>
            <div><Label className="text-xs">Plan</Label>
              <Select value={f.plan} onValueChange={(v) => upd("plan", v)}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["Starter", "Growth", "Professional", "Enterprise"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Monthly SaaS Fee (₹)</Label><Input type="number" className="rounded-sm" value={f.monthly_saas_fee} onChange={(e) => upd("monthly_saas_fee", e.target.value)} data-testid="tenant-fee" /></div>
            <div><Label className="text-xs">Start Date</Label><Input type="date" className="rounded-sm" value={f.start_date} onChange={(e) => upd("start_date", e.target.value)} /></div>
            <div><Label className="text-xs">Renewal Date</Label><Input type="date" className="rounded-sm" value={f.renewal_date} onChange={(e) => upd("renewal_date", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Account Limit</Label><Input type="number" className="rounded-sm" value={f.account_limit} onChange={(e) => upd("account_limit", e.target.value)} /></div>
            <div><Label className="text-xs">Comm. Credits</Label><Input type="number" className="rounded-sm" value={f.communication_credits} onChange={(e) => upd("communication_credits", e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm" data-testid="save-tenant-button">Create Tenant</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FEATURE_FLAGS = [
  { key: "export", label: "Data Export (CSV / Excel / PDF)" },
  { key: "integrations", label: "Integrations (WhatsApp / SMS / Email)" },
  { key: "advanced_reports", label: "Advanced Reports" },
  { key: "audit_logs", label: "Audit Logs" },
  { key: "api_access", label: "API Access" },
];

function FeatureAccess({ baseFlags = [], overrides, setOverrides }) {
  const grant = overrides.grant || [];
  const revoke = overrides.revoke || [];
  const stateOf = (k) => (grant.includes(k) ? "on" : revoke.includes(k) ? "off" : "default");
  const set = (k, mode) => {
    const g = grant.filter((x) => x !== k);
    const r = revoke.filter((x) => x !== k);
    if (mode === "on") g.push(k);
    if (mode === "off") r.push(k);
    setOverrides({ grant: g, revoke: r });
  };
  const effective = (k) => {
    const base = baseFlags.includes(k);
    const s = stateOf(k);
    return s === "on" || (base && s !== "off");
  };
  return (
    <div className="border border-border rounded-sm p-3 space-y-2.5 bg-secondary/30" data-testid="feature-access">
      <div className="label-caps">Feature Access — Manual Override</div>
      <p className="text-[11px] text-muted-foreground -mt-1">Overrides the plan default per tenant. Does not affect Razorpay billing.</p>
      {FEATURE_FLAGS.map((f) => {
        const s = stateOf(f.key);
        const base = baseFlags.includes(f.key);
        const eff = effective(f.key);
        return (
          <div key={f.key} className="flex items-center justify-between gap-2" data-testid={`flag-row-${f.key}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{f.label}</div>
              <div className="text-[11px] text-muted-foreground">Plan default: {base ? "Included" : "Not included"} · Effective: <span className={eff ? "text-[#16A34A] font-semibold" : "text-muted-foreground"}>{eff ? "On" : "Off"}</span></div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {["default", "on", "off"].map((m) => (
                <button key={m} type="button" onClick={() => set(f.key, m)} data-testid={`flag-${f.key}-${m}`}
                  className={`px-2 py-1 rounded-sm text-[11px] font-semibold border transition-colors ${s === m ? (m === "on" ? "bg-[#16A34A] text-white border-[#16A34A]" : m === "off" ? "bg-destructive text-white border-destructive" : "bg-[#0B3D91] text-white border-[#0B3D91]") : "bg-card border-border text-muted-foreground hover:border-[#0B3D91]/40"}`}>
                  {m === "default" ? "Default" : m === "on" ? "On" : "Off"}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EntitlementControl({ tenant, onDone }) {
  const [saving, setSaving] = useState(false);
  const ent = tenant.billing_entitlement || { plan_type: "standard", communication_billing: "standard", active: true };
  const isLifetime = ent.plan_type === "lifetime_free" && ent.active;
  const free = { total: tenant.free_comms_total ?? 0, used: tenant.free_comms_used ?? 0 };
  const freeRemaining = Math.max(0, (free.total || 0) - (free.used || 0));

  const setPlan = async (planType) => {
    const label = planType === "lifetime_free" ? "grant Lifetime Free (communication billing waived)" : "revoke Lifetime Free and restore standard billing";
    if (!window.confirm(`Are you sure you want to ${label} for "${tenant.company_name}"?`)) return;
    setSaving(true);
    try {
      await api.post(`/admin/tenants/${tenant.tenant_id}/entitlement`, { plan_type: planType });
      toast.success(planType === "lifetime_free" ? "Lifetime Free granted" : "Restored to standard billing");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed to update entitlement");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-sm p-3 space-y-2.5 bg-secondary/30" data-testid="entitlement-control">
      <div className="label-caps">Billing Entitlement</div>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            {isLifetime ? "Lifetime Free" : "Standard"}
            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${isLifetime ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid="entitlement-current">
              {isLifetime ? "Billing Waived" : "Standard Billing"}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {isLifetime
              ? "Communications never consume RC. Expiry: Never."
              : `Free communications: ${freeRemaining} of ${free.total || 0} remaining.`}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isLifetime ? (
          <Button size="sm" variant="outline" className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/10" disabled={saving} onClick={() => setPlan("standard")} data-testid="entitlement-revoke-lifetime">
            {saving ? "Saving…" : "Revoke Lifetime Free"}
          </Button>
        ) : (
          <Button size="sm" className="rounded-sm bg-[#16A34A] hover:bg-[#15803d]" disabled={saving} onClick={() => setPlan("lifetime_free")} data-testid="entitlement-grant-lifetime">
            {saving ? "Saving…" : "Grant Lifetime Free"}
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">Lifetime Free waives RecoverOS billing only. The tenant's own communication provider usage/cost is unaffected.</p>
    </div>
  );
}

function EditTenantDialog({ tenant, onDone, industries = [] }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [ov, setOv] = useState({ grant: [], revoke: [] });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const openDlg = () => {
    setF({
      logo: tenant.logo || "", plan: tenant.plan || "Starter", industry: tenant.industry || "nbfc",
      product_name: tenant.product_name || "", accent_color: tenant.accent_color || "",
      monthly_saas_fee: tenant.monthly_saas_fee ?? "", start_date: tenant.start_date || "",
      renewal_date: tenant.renewal_date || "", subscription_status: tenant.subscription_status || "active",
      account_limit: tenant.account_limit ?? "", communication_credits: tenant.communication_credits ?? "",
    });
    const o = tenant.feature_overrides || {};
    setOv({ grant: [...(o.grant || [])], revoke: [...(o.revoke || [])] });
    setOpen(true);
  };

  const submit = async () => {
    try {
      await api.patch(`/tenants/${tenant.tenant_id}`, { ...f, monthly_saas_fee: +f.monthly_saas_fee || 0, account_limit: +f.account_limit, communication_credits: +f.communication_credits, feature_overrides: ov });
      toast.success("Subscription updated");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={openDlg} data-testid={`edit-tenant-${tenant.tenant_id}`}>
        <PencilSimple size={14} weight="bold" /> Edit
      </Button>
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Subscription — {tenant.company_name}</DialogTitle>
          <DialogDescription>Update plan, billing and usage limits for this tenant.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <LogoUpload value={f.logo} onChange={(v) => upd("logo", v)} testId="edit-tenant-logo" />
          <div><Label className="text-xs">Industry / Vertical</Label>
            <Select value={f.industry} onValueChange={(v) => upd("industry", v)}>
              <SelectTrigger className="rounded-sm" data-testid="edit-tenant-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
              <SelectContent>{industries.map((i) => <SelectItem key={i.key} value={i.key}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <BrandingFields f={f} upd={upd} idPrefix="edit-tenant" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Plan</Label>
              <Select value={f.plan} onValueChange={(v) => upd("plan", v)}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["Starter", "Growth", "Professional", "Enterprise"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Subscription Status</Label>
              <Select value={f.subscription_status} onValueChange={(v) => upd("subscription_status", v)}>
                <SelectTrigger className="rounded-sm" data-testid="edit-tenant-sub-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["active", "trial", "past_due", "expired", "suspended"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Monthly Fee (₹)</Label><Input type="number" className="rounded-sm" value={f.monthly_saas_fee} onChange={(e) => upd("monthly_saas_fee", e.target.value)} data-testid="edit-tenant-fee" /></div>
            <div><Label className="text-xs">Start Date</Label><Input type="date" className="rounded-sm" value={f.start_date} onChange={(e) => upd("start_date", e.target.value)} /></div>
            <div><Label className="text-xs">Renewal Date</Label><Input type="date" className="rounded-sm" value={f.renewal_date} onChange={(e) => upd("renewal_date", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Account Limit</Label><Input type="number" className="rounded-sm" value={f.account_limit} onChange={(e) => upd("account_limit", e.target.value)} /></div>
            <div><Label className="text-xs">Comm. Credits</Label><Input type="number" className="rounded-sm" value={f.communication_credits} onChange={(e) => upd("communication_credits", e.target.value)} /></div>
          </div>
          <FeatureAccess baseFlags={tenant.base_flags || []} overrides={ov} setOverrides={setOv} />
          <EntitlementControl tenant={tenant} onDone={onDone} />
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm" data-testid="save-edit-tenant-button">Save Changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
